/**
 * Where use cases live: the built-ins shipped with the page (loaded as a
 * chunk of their own, told to listeners when they arrive), and yours, kept
 * in this browser's localStorage (every read and write guarded; with no
 * storage, yours last as long as the tab). Nothing kept or exported ever
 * holds a key or a secret: everything is scrubbed on the way in and out.
 * There are no share links; a use case travels as a file.
 */

import type { CreateUseCaseStore, UseCase, Unsub } from '../contracts.ts';
import { exportText, parseText, parseUseCase, scrub } from './format.ts';

export const STORE_KEY = 'schedule-bit.playground.usecases';

const defaultStorage = (): Storage | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

export const createUseCaseStore: CreateUseCaseStore = (storage) => {
  const store = storage === undefined ? defaultStorage() : storage;
  const listeners = new Set<() => void>();
  // The built-ins are a chunk of their own, loaded as the page starts; screens redraw when they come.
  let builtins: readonly UseCase[] = [];
  let builtinIds = new Set<string>();

  const load = (): UseCase[] => {
    try {
      const raw = store?.getItem(STORE_KEY);
      if (!raw) return [];
      const list: unknown = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      return list.map(parseUseCase).filter((uc): uc is UseCase => !('error' in uc));
    } catch {
      return [];
    }
  };
  let mine: UseCase[] = load();

  const persist = (): void => {
    try {
      store?.setItem(STORE_KEY, JSON.stringify(mine));
    } catch { /* full or blocked: they stay in this tab */ }
    for (const fn of [...listeners]) {
      try { fn(); } catch { /* a listener's fault is its own */ }
    }
  };

  /** An id nobody else holds: a built-in's id, or another of yours, gets a suffix. */
  const freeId = (id: string, except?: UseCase): string => {
    const taken = (candidate: string) => builtinIds.has(candidate) || mine.some((m) => m.id === candidate && m !== except);
    if (!taken(id)) return id;
    for (let n = 2; ; n += 1) {
      const candidate = `${id.slice(0, 56)}-${n}`;
      if (!taken(candidate)) return candidate;
    }
  };

  const save = (uc: UseCase): UseCase => {
    const existing = mine.find((m) => m.id === uc.id);
    // Only the page ships built-ins: a saved one is yours, and says so.
    const kept = scrub({ ...uc, origin: uc.origin === 'builtin' ? 'recorded' : uc.origin, id: existing ? uc.id : freeId(uc.id) });
    mine = existing ? mine.map((m) => (m === existing ? kept : m)) : [...mine, kept];
    persist();
    return kept;
  };

  const ready = import('./builtins.ts').then(
    (m) => {
      builtins = m.BUILTINS;
      builtinIds = new Set(builtins.map((b) => b.id));
      for (const fn of [...listeners]) {
        try { fn(); } catch { /* a listener's fault is its own */ }
      }
    },
    () => { /* the chunk did not load: yours still work */ },
  );

  return {
    builtins: () => builtins,
    ready: () => ready,
    mine: () => mine,
    save: (uc) => { save(uc); },
    remove(id) {
      const before = mine.length;
      mine = mine.filter((m) => m.id !== id);
      if (mine.length !== before) persist();
    },
    // Read only: the caller vets it and saves it after, so a file the vet refuses is never kept.
    importText(text) {
      const parsed = parseText(text);
      if ('error' in parsed) return parsed;
      return scrub({ ...parsed, origin: 'imported', id: freeId(parsed.id) });
    },
    exportText,
    on(fn): Unsub {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
  };
};
